import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { Tooltip as MuiTooltip, Alert, Snackbar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash, faCircleCheck, faPlus, faComments, faCircleXmark, faChartSimple, faGear, faCircleUp, faStar, faPenToSquare, faPen, faPlug, faToggleOn, faToggleOff, faUsers, faXmark, faCircle, faUserPlus, faCreditCard, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { authFetch } from '../utils/api';
import { deleteAgent } from '../utils/agentDeletion';
import { canWrite, reconcileSelectedBotId } from '../utils/permissions';
import { navigateExternal } from '../utils/navigation.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import ConfirmationModal from '../components/confirmationModal.js';
import WarningModal from '../components/WarningModal.js';
import AddBotModal from '../components/addBotModal.js';
import ChatModal from '../components/chatModal.js';
import LimitExceededModal from '../components/limitExceededModal.js';
import PaymentPastDueModal from '../components/paymentPastDueModal.js';
import SuccessScoreModal, { calculateSuccessScore, getScoreColorClass } from '../components/SuccessScoreModal.js';
import MetaIntegrationModal from '../components/MetaIntegrationModal.js';
import AppRatingModal from '../components/AppRatingModal.js';
import WhatsNewModal from '../components/WhatsNewModal.js';
import './bots.css';
import './sectionPages.css';
import { useTranslation } from 'react-i18next';
import '../translations.js';
import FirstBotPrompt from '../components/FirstBotPrompt.js';
import useUpgrade from '../hooks/useUpgrade.js';
import { useLiveNotifications } from '../components/LiveNotificationsProvider';
import InfoTooltip from '../components/InfoTooltip';
import '../components/liveInbox.css';

// The "what's new" announcement (WhatsNewModal) is switched off (Robert, 13.10.2026); true shows it again
// to everyone who has not dismissed it with "Do not show this again".
const WHATS_NEW_ENABLED = false;
// The "How do you like Ultimo Bots?" rating modal is switched off for everyone (Robert, 29.10.2026): it would stack
// with the LinkedIn bonus after a sign-in. true shows it again (once per page visit, accounts older than 12 hours).
const APP_RATING_ENABLED = false;

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY" || stored === "YYYY-MM-DD") {
    return stored;
  }
  return "YYYY-MM-DD";
};

const formatActivityKey = (key, pattern) => {
  const parts = key.split("-");
  if (parts.length !== 3) return key;
  const [mm, dd, yyyy] = parts;

  if (pattern === "DD-MM-YYYY") {
    return `${dd}-${mm}-${yyyy}`;
  }

  if (pattern === "MM-DD-YYYY") {
    return `${mm}-${dd}-${yyyy}`;
  }

  return `${yyyy}-${mm}-${dd}`;
};

// Build array of last 7 days in MM-DD-YYYY (oldest -> newest)
const getLast7Keys = () => {
  const keys = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = 13; i >= 0; i--) {
    const day = new Date(d);
    day.setDate(d.getDate() - i);
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    const yyyy = String(day.getFullYear());
    keys.push(`${mm}-${dd}-${yyyy}`);
  }
  return keys;
};

const getActivityMap = (bot) => {
  let m = bot?.activity_last_7_days;
  if (!m) return {};
  if (typeof m === 'string') {
    try { m = JSON.parse(m); } catch { return {}; }
  }
  return (m && typeof m === 'object') ? m : {};
};

// Recharts-friendly data: [{ date: 'MM-DD-YYYY', value: n }, ...]
const getActivityData = (bot) => {
  const map = getActivityMap(bot);
  const format = getUserDateFormat();
  return getLast7Keys().map(k => ({
    date: k,
    displayDate: formatActivityKey(k, format),
    value: Number(map[k] ?? 0)
  }));
};

// Minimal tooltip
const ActivityTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="activity-tooltip">
        <div className="activity-tooltip-date">{label}</div>
        <div className="activity-tooltip-value">{payload[0].value}</div>
      </div>
    );
  }
  return null;
};

// Single, full-size line chart filling its parent div
const ActivityLine = ({ bot }) => {
  const data = getActivityData(bot);
  return (
    <div className="activity-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 0, bottom: 6, left: 0 }}>
          <XAxis dataKey="displayDate" hide />
          <YAxis hide allowDecimals={false} domain={['dataMin - 1', 'dataMax + 1']} padding={{ top: 2, bottom: 2 }} />
          <Tooltip content={<ActivityTooltip />} />
          <Line
            type="natural"
            dataKey="value"
            stroke="#5616ea"
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Map backend status_display className to star icon CSS class
const STATUS_TO_STAR_CLASS = {
  active: 'star-icon-pro-subscription',
  trialing: 'star-icon-trialing',
  ending: 'star-icon-expires',
  cancelled: 'star-icon-cancelled',
  'past-due': 'star-icon-past-due',
  unknown: 'star-icon-pro-subscription',
};

const formatTimeAgo = (dateStr, t) => {
  if (!dateStr) return null;
  const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return t('time_just_now');
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t('time_today');
  if (diffDays < 30) return t('time_n_days_ago', { count: diffDays });
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return t('time_n_months_ago', { count: diffMonths });
  const diffYears = Math.floor(diffDays / 365);
  return t('time_n_years_ago', { count: diffYears });
};

const BotsOverview = ({ onSettingsClick, onAnalyticsClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedBotId, refreshBots } = useSelectedBot();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [botToDelete, setBotToDelete] = useState(null);
  const [deletionNotice, setDeletionNotice] = useState(false);
  const [webflowDeletedSites, setWebflowDeletedSites] = useState([]);
  const [botToChat, setBotToChat] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openChatModal, setOpenChatModal] = useState(false);
  const [editingBotId, setEditingBotId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [savingBotNameId, setSavingBotNameId] = useState(null);
  // Set on Escape so the input's blur handler cancels instead of saving.
  const renameCancelRef = useRef(false);
  const [openLimitExceededModal, setOpenLimitExceededModal] = useState(false);
  const [exceededBots, setExceededBots] = useState([]);
  const hasShownLimitOnceRef = useRef(false);
  const [openPastDueModal, setOpenPastDueModal] = useState(false);
  const [pastDueBots, setPastDueBots] = useState([]);
  const hasShownPastDueOnceRef = useRef(false);

  const [successScoreBot, setSuccessScoreBot] = useState(null);
  const [openSuccessScoreModal, setOpenSuccessScoreModal] = useState(false);
  const [successDataMap, setSuccessDataMap] = useState({});
  // Agent online/offline status per bot (Agent availability toggle).
  const [agentOnlineStatus, setAgentOnlineStatus] = useState({});
  // Per-bot full team roster (every org member + their online flag for
  // that bot). Fetched once after the agent_status load so the bot card
  // can show a compact "X / Y online" chip without making the user open
  // the Live Inbox to check teammate availability.
  const [botRosters, setBotRosters] = useState({});
  // Bot whose team roster modal is currently open.
  const [rosterModalBot, setRosterModalBot] = useState(null);
  // True until the initial GET /live/agent_status call resolves. Used
  // together with `rosterLoadedBots` to render a single spinner in the
  // availability row until both data sources are in.
  const [agentStatusLoading, setAgentStatusLoading] = useState(true);
  // Set of bot ids whose initial roster has been loaded at least once.
  const [rosterLoadedBots, setRosterLoadedBots] = useState(() => new Set());
  const [togglingAgentStatus, setTogglingAgentStatus] = useState({});
  // Per-(bot,user) busy flag while admins are force-offlining a teammate
  // from the team availability modal. Same shape as LiveInbox.
  const [forcingOffline, setForcingOffline] = useState({});
  // Pending confirmation for force-offlining a teammate. When set, the
  // shared WarningModal is shown to confirm the destructive action.
  const [pendingForceOffline, setPendingForceOffline] = useState(null);
  // Provider's heartbeat registrar - keeps the agent flagged online on
  // the server every 30s so the backend reaper doesn't flip the row to
  // offline after 90s of silence.
  const { setAgentOnline: registerAgentHeartbeat } = useLiveNotifications();
  const [openMetaIntegrationModal, setOpenMetaIntegrationModal] = useState(false);
  const [openAppRatingModal, setOpenAppRatingModal] = useState(false);
  const [openWhatsNewModal, setOpenWhatsNewModal] = useState(false);
  // Set as soon as we decide the "what's new" announcement will show, so the
  // app-rating modal (which fires a bit later) doesn't stack on top of it.
  const whatsNewActiveRef = useRef(false);
  const [userId, setUserId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);

  // Close the three-dot menu when clicking anywhere outside it.
  useEffect(() => {
    if (!menuOpenId) return;
    const close = () => setMenuOpenId(null);
    const id = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(id); document.removeEventListener('click', close); };
  }, [menuOpenId]);

  const onUpgradeComplete = async () => {
    setLoading(true);
    const refreshedBots = await fetchBots();
    setBots(refreshedBots);
    setLoading(false);
  };

  const { handleUpgradeClick: upgradeClick, UpgradeModal, isUpgradeLoading } = useUpgrade({ onComplete: onUpgradeComplete });

  const handleUpgradeClick = async (bot) => {
    setOpenChatModal(false);
    await upgradeClick(bot);
  };

  // Past-due payment recovery. We open the Stripe customer portal's
  // payment_method_update flow so the user lands directly on the
  // "update card" screen; Stripe then automatically retries the open
  // invoice with the new payment method (no new subscription created,
  // no double-billing).
  const [updatePaymentLoadingId, setUpdatePaymentLoadingId] = useState(null);
  const handleUpdatePayment = async (bot) => {
    if (!canWrite()) {
      window.dispatchEvent(
        new CustomEvent('ub:permission_denied', { detail: { message: '' } })
      );
      return;
    }
    const stripeCustomerId = bot?.subscription?.stripe_customer_id || null;
    const stripeSubscriptionId = bot?.subscription?.stripe_subscription_id || null;
    // We let the backend resolve the customer from the subscription if
    // the cached customer id is NULL (older rows). Only bail out when
    // we genuinely have nothing to give the portal.
    if (!stripeCustomerId && !stripeSubscriptionId) return;
    setUpdatePaymentLoadingId(bot.id);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}create-customer-portal-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stripe_customer_id: stripeCustomerId,
            stripe_subscription_id: stripeSubscriptionId,
          }),
        }
      );
      const data = await res.json();
      if (data?.url) {
        navigateExternal(data.url);
      }
    } catch (err) {
      console.error('Failed to open Stripe payment-update portal:', err);
    } finally {
      setUpdatePaymentLoadingId(null);
    }
  };

  const findExceededBots = (list) =>
  (list || []).filter(
      (b) =>
      b?.subscription_plan &&
      typeof b.current_message_count === 'number' &&
      typeof b.subscription_plan.message_limit === 'number' &&
      b.current_message_count >= b.subscription_plan.message_limit
  );

  const findPastDueBots = (list) =>
  (list || []).filter(
      (b) =>
      b?.subscription?.status === 'past_due' ||
      b?.status_display?.className === 'past-due'
  );

  useEffect(() => {
    const fetchInitialBots = async () => {
      const data = await fetchBots();
      setBots(data);

      const exceeded = findExceededBots(data);
      setExceededBots(exceeded);
      if (exceeded.length > 0 && !hasShownLimitOnceRef.current) {
        setOpenLimitExceededModal(true);
        hasShownLimitOnceRef.current = true;
      }

      const pastDue = findPastDueBots(data);
      setPastDueBots(pastDue);
      if (pastDue.length > 0 && !hasShownPastDueOnceRef.current) {
        setOpenPastDueModal(true);
        hasShownPastDueOnceRef.current = true;
      }

      // Reconcile stored selectedBotId against the workspace's bots
      // so Settings / Integration / LiveChat / Activity never inherit
      // a stale id (previous workspace, deleted bot, tampered storage)
      // — which would 403 the first request and break those pages.
      reconcileSelectedBotId(data || []);
      setLoading(false);
    };
    fetchInitialBots();
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("user_id")) return;

    let socket;
    let reconnectTimer = null;
    const connectWebSocket = () => {
      socket = new WebSocket(`${process.env.REACT_APP_API_WEBSOCKET_URL}`);

      socket.onopen = () => {
        const userId = localStorage.getItem("user_id");
        socket.send(JSON.stringify({ type: "init", user_id: userId, token: localStorage.getItem('token') }));
      };

      socket.onmessage = (event) => {
        try {
          const receivedData = JSON.parse(event.data);

          if (receivedData.bot_id) {
            setBots((prevBots) =>
              prevBots.map((bot) =>
                bot.id === receivedData.bot_id
                  ? {
                      ...bot,
                      creation_percentage: receivedData.percentage !== undefined ? receivedData.percentage : bot.creation_percentage,
                      status: receivedData.status !== undefined ? receivedData.status : bot.status,
                    }
                  : bot
              )
            );
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      socket.onclose = (evt) => {
        if (evt && evt.code === 4401) {
          console.error('bots WS closed 4401 (auth failure); not reconnecting');
          return;
        }
        reconnectTimer = setTimeout(connectWebSocket, 5000);
      };
    };

    connectWebSocket();
    return () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (socket) {
        socket.onclose = null;
        try { socket.close(); } catch {}
      }
    };
  }, []);

  useEffect(() => {
    const exceeded = findExceededBots(bots);
    setExceededBots(exceeded);
  }, [bots]);

  // Legacy entry flag from other pages: send the user into the agent-creation flow.
  useEffect(() => {
    const trigger = localStorage.getItem('triggerAddBotModal');
    if (trigger === '1') {
      localStorage.removeItem('triggerAddBotModal');
      navigate('/agents/new', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build success data map from bot.success_score when bots are loaded
  useEffect(() => {
    const newDataMap = {};
    for (const bot of bots) {
      if ((bot.status === 'completed' || bot.status === 'no_content_found') && bot.success_score) {
        newDataMap[bot.id] = bot.success_score;
      }
    }
    setSuccessDataMap(newDataMap);
  }, [bots]);

  // Fetch initial agent online/offline status (one call returns all bots).
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_BASE_URL}live/agent_status`);
        if (res && res.ok) {
          const data = await res.json();
          const statuses = data.statuses || {};
          setAgentOnlineStatus(statuses);
          // Seed the heartbeat ticker for any bot already marked online
          // server-side so the 90s reaper doesn't flip them to offline
          // before the next user-driven toggle.
          for (const [botId, online] of Object.entries(statuses)) {
            if (online) registerAgentHeartbeat(botId, true);
          }
        }
      } catch (err) {
        // ignore
      } finally {
        setAgentStatusLoading(false);
      }
    })();
  }, [registerAgentHeartbeat]);

  // Fetch per-bot team rosters in parallel so each card can show a
  // compact "X / Y online" chip with a tooltip / modal listing every
  // teammate. We refresh on the same 30s cadence as Live Inbox so
  // counts stay roughly in sync without being noisy.
  useEffect(() => {
    const completedBots = bots.filter(
      (b) => b.status === 'completed' || b.status === 'no_content_found'
    );
    if (completedBots.length === 0) return;
    let cancelled = false;
    const load = async () => {
      // One request per 50 bots (an account can have thousands), each chunk
      // streamed into state as soon as it returns so a slow chunk does not
      // keep every other bot's chip on the loading spinner.
      const chunks = [];
      for (let i = 0; i < completedBots.length; i += 50) chunks.push(completedBots.slice(i, i + 50));
      await Promise.all(
        chunks.map(async (chunk) => {
          try {
            const ids = chunk.map((bot) => bot.id);
            const res = await authFetch(
              `${process.env.REACT_APP_API_BASE_URL}live/agents?bot_ids=${encodeURIComponent(ids.join(','))}`
            );
            if (!res || !res.ok) return;
            const data = await res.json();
            if (cancelled) return;
            setBotRosters((prev) => {
              const next = { ...prev };
              for (const id of ids) next[id] = Array.isArray(data[id]) ? data[id] : [];
              return next;
            });
            for (const id of ids) {
              const self = (data[id] || []).find((agent) => agent.is_self);
              if (self) {
                setAgentOnlineStatus((prev) => ({ ...prev, [id]: self.online }));
                registerAgentHeartbeat(id, self.online);
              }
            }
            setRosterLoadedBots((prev) => {
              const next = new Set(prev);
              ids.forEach((id) => next.add(id));
              return next;
            });
          } catch (_e) { /* swallow */ }
        })
      );
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [bots, registerAgentHeartbeat]);

  const handleToggleAgentOnline = async (botId) => {
    const newStatus = agentOnlineStatus[botId] ? 'offline' : 'online';
    setTogglingAgentStatus((prev) => ({ ...prev, [botId]: true }));
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live/agent_status/${botId}`,
        { method: 'PUT', body: JSON.stringify({ status: newStatus }) }
      );
      if (res && res.ok) {
        setAgentOnlineStatus((prev) => ({ ...prev, [botId]: newStatus === 'online' }));
        // Keep the local team roster cache in sync so the chip count
        // reflects the change immediately instead of waiting for the
        // next 30s poll.
        setBotRosters((prev) => {
          const list = prev[botId];
          if (!list) return prev;
          return {
            ...prev,
            [botId]: list.map((a) =>
              a.is_self ? { ...a, online: newStatus === 'online' } : a
            ),
          };
        });
        // Keep the heartbeat ticker in sync with the toggle.
        registerAgentHeartbeat(botId, newStatus === 'online');
      }
    } catch (err) {
      console.error('Error toggling agent status:', err);
    }
    setTogglingAgentStatus((prev) => ({ ...prev, [botId]: false }));
  };

  // Force a teammate offline from the team availability modal. Mirrors
  // LiveInbox.handleForceTeammateOffline so behaviour and permissions
  // are identical.
  const handleForceTeammateOffline = async (botId, userId) => {
    if (!canWrite()) return; // backend also enforces
    const key = `${botId}|${userId}`;
    setForcingOffline((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live/agent_status/${botId}/user/${userId}`,
        { method: 'PUT', body: JSON.stringify({ status: 'offline' }) }
      );
      if (res && res.ok) {
        setBotRosters((prev) => {
          const list = prev[botId];
          if (!list) return prev;
          return {
            ...prev,
            [botId]: list.map((a) =>
              a.user_id === userId ? { ...a, online: false } : a
            ),
          };
        });
      }
    } catch (_) { /* swallow */ }
    setForcingOffline((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Check if the "what's new" agentic-upgrade modal should be shown. One-time
  // announcement: shows until the user dismisses it with "do not show again"
  // (persisted in user_modal_preferences, modal_name = 'agentic_upgrade_modal').
  useEffect(() => {
    const checkWhatsNewModal = async () => {
      const userIdFromStorage = localStorage.getItem('user_id');
      if (!userIdFromStorage) return;

      setUserId(parseInt(userIdFromStorage, 10));
      if (!WHATS_NEW_ENABLED) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}user_modal_preferences/${userIdFromStorage}/check/agentic_upgrade_modal`
        );

        if (response.ok) {
          const data = await response.json();
          if (!data.is_dismissed) {
            whatsNewActiveRef.current = true;
            // Short delay so it doesn't overwhelm the user on page load.
            setTimeout(() => setOpenWhatsNewModal(true), 300);
          }
        }
      } catch (error) {
        console.error('Error checking whats-new modal preference:', error);
      }
    };

    checkWhatsNewModal();
  }, []);

  // Check if meta integration modal should be shown
  useEffect(() => {
    const checkMetaIntegrationModal = async () => {
      const userIdFromStorage = localStorage.getItem('user_id');
      if (!userIdFromStorage) return;

      setUserId(parseInt(userIdFromStorage, 10));

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}user_modal_preferences/${userIdFromStorage}/check/meta_integration_modal`
        );

        if (response.ok) {
          const data = await response.json();
          if (!data.is_dismissed) {
            // Show modal after a short delay so it doesn't overwhelm user on page load
            setTimeout(() => {
              setOpenMetaIntegrationModal(false);
            }, 500);
          }
        }
      } catch (error) {
        console.error('Error checking meta integration modal preference:', error);
      }
    };

    checkMetaIntegrationModal();
  }, []);

  // Check if app rating modal should be shown
  useEffect(() => {
    const EXCLUDED_USER_IDS = [];
    const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

    const checkAppRatingModal = async () => {
      if (!APP_RATING_ENABLED) return;
      const userIdFromStorage = localStorage.getItem('user_id');
      if (!userIdFromStorage) return;

      const uid = parseInt(userIdFromStorage, 10);
      if (EXCLUDED_USER_IDS.includes(uid)) return;

      try {
        // Check if already dismissed
        const prefResp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}user_modal_preferences/${userIdFromStorage}/check/app_rating_modal`
        );
        if (prefResp.ok) {
          const prefData = await prefResp.json();
          if (prefData.is_dismissed) return;
        }

        // Check if account is at least 12 hours old
        const token = localStorage.getItem('token');
        const userResp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}users/${userIdFromStorage}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (userResp.ok) {
          const userData = await userResp.json();
          const createdAt = new Date(userData.created_at);
          const now = new Date();
          if (now - createdAt < TWELVE_HOURS_MS) return;
        } else {
          return;
        }

        // Don't stack on top of the one-time "what's new" announcement.
        setTimeout(() => {
          if (whatsNewActiveRef.current) return;
          setOpenAppRatingModal(true);
        }, 1500);
      } catch (error) {
        console.error('Error checking app rating modal:', error);
      }
    };

    checkAppRatingModal();
  }, []);

  const fetchBots = async () => {
    try {
      // include_activity dropped: the per-card sparkline (ActivityLine) is not
      // rendered, so this list was computing a chat_history aggregation for
      // every bot and discarding it. The Overview chart loads activity from the
      // dedicated /bots/activity endpoint instead.
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id")}&include_knowledge=False&include_success_score=True`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching bots:', err);
      return [];
    }
  };

  const updateBotName = async (botId, newName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}bots/${botId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      if (!response.ok) {
        throw new Error(`Failed to update bot. Status: ${response.status}`);
      }
      const updated = await response.json();
      setBots((prevBots) =>
        prevBots.map((b) => (b.id === updated.id ? { ...b, name: updated.name } : b))
      );
    } catch (err) {
      console.error('Error updating bot:', err);
    }
  };

  const handleDeleteClick = (bot) => {
    setBotToDelete(bot);
    setOpenModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!botToDelete) return;
    setLoading(true);

    try {
      const { webflowSites = [] } = (await deleteAgent(botToDelete.id)) || {};
      if (webflowSites.length) setWebflowDeletedSites(webflowSites);

      const selectedBotIds = JSON.parse(localStorage.getItem('selectedBotIds') || '[]');
      const selectedBotId = localStorage.getItem('selectedBotId') || '';
      const updatedBotIds = selectedBotIds.filter(id => id !== botToDelete.id);
      localStorage.setItem('selectedBotIds', JSON.stringify(updatedBotIds));

      if (selectedBotId === botToDelete.id) {
        localStorage.removeItem('selectedBotId');
      }

      // Tear down the agent-online heartbeat ticker for this bot.
      // Otherwise the provider keeps POSTing /live/agent_heartbeat every
      // 30s for a bot_id the backend no longer knows about, producing
      // 404 spam in the console + server logs.
      setAgentOnlineStatus((prev) => {
        const next = { ...prev };
        delete next[botToDelete.id];
        return next;
      });
      registerAgentHeartbeat(botToDelete.id, false);

      const refreshedBots = await fetchBots();
      setBots(refreshedBots);
    } catch (err) {
      setDeletionNotice(true);
    } finally {
      setLoading(false);
      setOpenModal(false);
      setBotToDelete(null);
    }
  };

  const handleAddBotClick = () => {
    // Read-only teammates aren't allowed to create bots. Surface the same
    // unified "permission denied" modal that backend 403s would trigger,
    // instead of opening the create-bot dialog and letting the POST fail.
    if (!canWrite()) {
      window.dispatchEvent(
        new CustomEvent('ub:permission_denied', { detail: { message: '' } })
      );
      return;
    }
    // New agents are created in the guided flow (/agents/new); AddBotModal stays
    // mounted only as the legacy fallback and is no longer opened from here.
    navigate('/agents/new');
  };

  // Let other surfaces open the create-agent dialog while this page is mounted
  // (e.g. the sidebar's "+ New Agent" button when the user is already on
  // /my-bots). Routed through handleAddBotClick so the same read-only
  // permission guard applies as the in-page button. A ref keeps the listener
  // subscription stable while always calling the latest handler.
  const addBotClickRef = useRef(handleAddBotClick);
  addBotClickRef.current = handleAddBotClick;
  useEffect(() => {
    const open = () => addBotClickRef.current();
    window.addEventListener('ub:open-add-bot', open);
    return () => window.removeEventListener('ub:open-add-bot', open);
  }, []);

  const handleOpenChatClick = (bot) => {
    setBotToChat(bot);
    setOpenChatModal(true);

    // Fetch updated bot data in background
    fetchBots().then(refreshedBots => {
      setBots(refreshedBots);
      const updatedBot = refreshedBots.find(b => b.id === bot.id);
      if (updatedBot) {
        setBotToChat(updatedBot);
      }
    });
  };

  const handleOpenIntegrationClick = (bot) => {
    navigate(`/integration?bot_id=${bot.id}`);
  };

  const handleChatModalClosed = async () => {
    setOpenChatModal(false);
    setLoading(true);
    const refreshedBots = await fetchBots();
    setBots(refreshedBots);
    setLoading(false);
  };

  const handleAddBotSubmit = async (formData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}create_chatbot`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to add bot. Status: ${response.status}`);
      }

      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const refreshedBots = await fetchBots();
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'bot_creation_started' });
      }
      setLoading(false);
      setBots(refreshedBots);
    } catch (err) {
      console.error('Error adding bot:', err);
    } finally {
      setOpenAddModal(false);
    }
  };

  const startEditing = (bot) => {
    setEditingBotId(bot.id);
    setEditedName(bot.name || bot.domain_name);
  };

  const commitEditing = async (bot) => {
    const trimmed = editedName.trim();

    if (trimmed.length === 0) {
      setEditingBotId(null);
      setEditedName('');
      return;
    }

    setEditingBotId(null);
    setEditedName('');

    if (trimmed !== (bot.name || bot.domain_name)) {
      setSavingBotNameId(bot.id);
      await updateBotName(bot.id, trimmed);
      setSavingBotNameId(null);
    }
  };

  const handleSuccessScoreClick = (bot) => {
    setSuccessScoreBot(bot);
    setOpenSuccessScoreModal(true);
  };

  const handleSettingsWithTab = (bot, tab) => {
    onSettingsClick(bot, tab);
  };

  // Enter a bot's section pages. The global SelectedBot context only fetches
  // on mount and keeps ready bots, so a just-built first agent isn't in it yet
  // — refresh before navigating, otherwise Overview shows the empty state.
  const openBotOverview = (bot) => {
    setSelectedBotId(String(bot.id));
    refreshBots();
    navigate('/overview');
  };

  const handleCardClick = (e, bot) => {
    if (editingBotId === bot.id) return; // renaming this card — don't navigate
    if (e.target.closest('button, a, input')) return;
    if (bot.status !== 'completed' && bot.status !== 'no_content_found') return;
    openBotOverview(bot);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <>
        <FirstBotPrompt onAddBot={handleAddBotClick} />
        <AddBotModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          onSubmit={handleAddBotSubmit}
        />
      </>
    );
  }

  return (
    <div className="bots-overview">
      <Snackbar open={deletionNotice} onClose={() => setDeletionNotice(false)}>
        <Alert severity="warning" onClose={() => setDeletionNotice(false)}>{t('mcp_delete_not_completed')}</Alert>
      </Snackbar>
      {/* Webflow: the deleted agent's script came off these sites; it leaves
          the live site with the next publish (Designer). */}
      <Snackbar open={webflowDeletedSites.length > 0} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setWebflowDeletedSites([])} data-testid="wf-deleted-agent-notice">
          {t('wf_agent_deleted_publish', { sites: webflowDeletedSites.map((x) => x.name || x.id).join(', ') })}
        </Alert>
      </Snackbar>
      <div>
        <header className="section-head">
          <h1 className="section-head-title">{t('your_bots')}</h1>
          <p className="section-head-sub">
            {t('bots_overview_subtitle')}
          </p>
        </header>
        <div className="bots-overview-toolbar">
          <button
            className="add-bot-btn"
            onClick={handleAddBotClick}
          >
            <FontAwesomeIcon icon={faPlus} /> {t('add_bot')}
          </button>
        </div>
        <div className="bot-container">
          {bots.map((bot) => {
            const isPastDue = bot?.subscription?.status === 'past_due' || bot?.status_display?.className === 'past-due';
            const isReady = bot.status === 'completed' || bot.status === 'no_content_found';
            const isBuilding = !isReady && bot.status !== 'failed';
            // Clamp to 0–100 and tolerate null/undefined so the ring + label
            // never break (creation_percentage is a 0–100 REAL from the API).
            const buildPct = Math.max(0, Math.min(100, Number(bot.creation_percentage) || 0));
            const isMenuOpen = menuOpenId === bot.id;

            const msgCount = bot.current_message_count ?? 0;
            const roster = botRosters[bot.id];
            const totalAgents = roster ? roster.length : 0;
            const onlineCount = roster ? roster.filter(a => a.online).length : 0;
            const rosterReady = rosterLoadedBots.has(bot.id);
            const isAgentLoading = agentStatusLoading || !rosterReady;

            const timeAgoStr = bot.created_at ? formatTimeAgo(bot.created_at, t) : null;

            const initial = (bot.name || bot.domain_name || 'A')[0].toUpperCase();
            const displayName = bot.name || bot.domain_name || t('bots_default_agent_name');
            const screenshotSrc = bot.website_desktop_screenshot_img_path || null;

            const isLimitReached = !isPastDue
              && typeof bot.current_message_count === 'number'
              && typeof bot.subscription_plan?.message_limit === 'number'
              && bot.current_message_count >= bot.subscription_plan.message_limit;

            // The chat-widget mockup — shown on its own when there's no
            // screenshot, and overlaid (centered, anchored to the bottom) on
            // the blurred screenshot when there is one.
            const chatWidgetInner = (
              <>
                <div className="bot-card-chat-header">
                  <div className="bot-card-chat-avatar">{initial}</div>
                  <span className="bot-card-chat-name">
                    {displayName.length > 18 ? displayName.slice(0, 18) + '…' : displayName}
                  </span>
                </div>
                <div className="bot-card-bubble bot-card-bubble--bot" />
                <div className="bot-card-bubble bot-card-bubble--user" />
                <div className="bot-card-bubble bot-card-bubble--bot" style={{ width: '52%' }} />
              </>
            );

            return (
              <div
                key={bot.id}
                className={`bot-card${isReady ? ' is-selectable' : ''}${isBuilding ? ' building' : ''}${isMenuOpen ? ' menu-open' : ''}`}
                onClick={(e) => handleCardClick(e, bot)}
              >
                {/* Preview */}
                <div className={`bot-card-preview${isReady && screenshotSrc ? ' has-shot' : ''}`}>
                  {isBuilding ? (
                    <div className="bot-card-preview-state">
                      <div className="progress-spinner">
                        <svg viewBox="0 0 36 36" className="circular-progress">
                          <circle className="circle-bg" cx="18" cy="18" r="15.916" />
                          <circle className="circle" cx="18" cy="18" r="15.916"
                            strokeDasharray="100"
                            strokeDashoffset={100 - buildPct}
                          />
                        </svg>
                        <div className="percentage-text">{buildPct.toFixed(0)}%</div>
                      </div>
                      <span className="bot-card-preview-label">{t('bots_building_agent')}</span>
                    </div>
                  ) : bot.status === 'failed' ? (
                    <div className="bot-card-preview-state">
                      <FontAwesomeIcon icon={faCircleXmark} style={{ fontSize: 32, color: '#ef4444' }} />
                      <span className="bot-card-preview-label" style={{ color: '#fca5a5' }}>{t('bots_build_failed')}</span>
                    </div>
                  ) : screenshotSrc ? (
                    <>
                      <img
                        className="bot-card-preview-shot"
                        src={screenshotSrc}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                      <div className="bot-card-chat-widget">
                        {chatWidgetInner}
                      </div>
                    </>
                  ) : (
                    <div className="bot-card-chat-widget">
                      {chatWidgetInner}
                    </div>
                  )}

                  {isPastDue && <span className="bot-card-plan-badge past-due">{t('bots_badge_past_due')}</span>}
                  {isLimitReached && <span className="bot-card-plan-badge limit">{t('bots_badge_limit_reached')}</span>}
                </div>

                {/* Info */}
                <div className="bot-card-info">
                  <div className="bot-card-name-row">
                    <div className="bot-card-name-block">
                      {editingBotId === bot.id ? (
                        <input
                          className="bot-card-name-input"
                          value={editedName}
                          autoFocus
                          maxLength={80}
                          disabled={savingBotNameId === bot.id}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setEditedName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              renameCancelRef.current = true;
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={() => {
                            if (renameCancelRef.current) {
                              renameCancelRef.current = false;
                              setEditingBotId(null);
                              setEditedName('');
                              return;
                            }
                            commitEditing(bot);
                          }}
                        />
                      ) : savingBotNameId === bot.id ? (
                        <p className="bot-card-name">
                          <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea' }} />
                        </p>
                      ) : (
                        <p className="bot-card-name">{displayName}</p>
                      )}
                      {timeAgoStr && (
                        <p className="bot-card-trained">{t('bots_created_ago', { time: timeAgoStr })}</p>
                      )}
                    </div>
                    <div className="bot-card-menu-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="bot-card-menu-btn"
                        onClick={(e) => { e.stopPropagation(); if (isBuilding) return; setMenuOpenId(isMenuOpen ? null : bot.id); }}
                        disabled={isBuilding}
                        title={isBuilding ? t('bots_menu_building_disabled') : undefined}
                        aria-label={t('bots_agent_options_aria')}
                      >
                        <svg
                          className="bot-card-menu-dots"
                          viewBox="0 0 25 7"
                          width="25"
                          height="7"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <circle cx="3.5" cy="3.5" r="2.5" />
                          <circle cx="12.5" cy="3.5" r="2.5" />
                          <circle cx="21.5" cy="3.5" r="2.5" />
                        </svg>
                      </button>
                      {isMenuOpen && !isBuilding && (
                        <div className="bot-card-menu">
                          {isReady && (
                            <button
                              type="button"
                              className="bot-card-menu-item"
                              onClick={() => { setMenuOpenId(null); openBotOverview(bot); }}
                            >
                              <FontAwesomeIcon icon={faGear} />
                              <span>{t('bots_menu_manage')}</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="bot-card-menu-item"
                            onClick={() => { setMenuOpenId(null); startEditing(bot); }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                            <span>{t('bots_menu_rename')}</span>
                          </button>
                          <button
                            type="button"
                            className="bot-card-menu-item danger"
                            onClick={() => { setMenuOpenId(null); handleDeleteClick(bot); }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            <span>{t('bots_menu_delete_agent')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {isReady && (
                    <button
                      type="button"
                      className="bot-card-configure-btn"
                      onClick={(e) => { e.stopPropagation(); openBotOverview(bot); }}
                    >
                      <FontAwesomeIcon icon={faGear} />
                      {t('bots_menu_manage')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ConfirmationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName={botToDelete?.domain_name}
        requireTextConfirmation={true}
      />
      <AddBotModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSubmit={handleAddBotSubmit}
      />
      {openChatModal && botToChat && (
        <ChatModal
          open={openChatModal}
          onClose={handleChatModalClosed}
          bot={botToChat}
          currentMessageCount={botToChat.current_message_count ?? 0}
          messagePlanLimit={botToChat.subscription_plan?.message_limit ?? 999999}
          planName={botToChat.subscription_plan?.name ?? 'Free'}
          openSubscriptionModalFromChat={handleUpgradeClick}
        />
      )}
      {UpgradeModal}

     <LimitExceededModal
       open={openLimitExceededModal}
       onClose={() => setOpenLimitExceededModal(false)}
       bots={exceededBots}
       onUpgrade={handleUpgradeClick}
     />

     <PaymentPastDueModal
       open={openPastDueModal}
       onClose={() => setOpenPastDueModal(false)}
       bots={pastDueBots}
       onUpdatePayment={handleUpdatePayment}
       updatePaymentLoadingId={updatePaymentLoadingId}
     />

     {openSuccessScoreModal && successScoreBot && (
       <SuccessScoreModal
         open={openSuccessScoreModal}
         onClose={() => setOpenSuccessScoreModal(false)}
         bot={successScoreBot}
         successData={successDataMap[successScoreBot.id] || {}}
         onSettingsClick={handleSettingsWithTab}
         onUpgradeClick={handleUpgradeClick}
       />
     )}

     <MetaIntegrationModal
       open={openMetaIntegrationModal}
       onClose={() => setOpenMetaIntegrationModal(false)}
       userId={userId}
     />

     <AppRatingModal
       open={openAppRatingModal}
       onClose={() => setOpenAppRatingModal(false)}
       userId={userId}
     />

     <WhatsNewModal
       open={openWhatsNewModal}
       onClose={() => setOpenWhatsNewModal(false)}
       userId={userId}
     />

      {/* Team availability modal — opens from the "X / Y" chip on a
          bot card. Mirrors LiveInbox: self can toggle on/off, admins
          can force a teammate offline (but never online), others are
          read-only. */}
      {rosterModalBot && (() => {
        const roster = botRosters[rosterModalBot.id] || [];
        const sorted = [...roster].sort((a, b) => {
          if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
          if (a.online !== b.online) return a.online ? -1 : 1;
          const an = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
          const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
          return an.localeCompare(bn);
        });
        const onlineCount = sorted.filter((a) => a.online).length;
        const selfAgent = sorted.find((a) => a.is_self);
        const otherAgents = sorted.filter((a) => !a.is_self);
        const renderRow = (a) => {
          const displayName = (a.first_name || a.last_name)
            ? `${a.first_name || ''} ${a.last_name || ''}`.trim()
            : (a.email || `User #${a.user_id}`);
          const key = `${rosterModalBot.id}|${a.user_id}`;
          const isSelf = a.is_self;
          const canForceOff = !isSelf && a.online && canWrite();
          const interactive = isSelf || canForceOff;
          const busy = isSelf
            ? !!togglingAgentStatus[rosterModalBot.id]
            : !!forcingOffline[key];
          const onClick = () => {
            if (!interactive || busy) return;
            if (isSelf) {
              handleToggleAgentOnline(rosterModalBot.id);
            } else {
              setPendingForceOffline({
                botId: rosterModalBot.id,
                userId: a.user_id,
                name: displayName,
              });
            }
          };
          return (
            <li key={a.user_id} className="live-inbox-agent-row">
              <span className={`live-inbox-agent-dot ${a.online ? 'online' : 'offline'}`} />
              <span className="live-inbox-agent-name" title={a.email || ''}>
                {displayName}
              </span>
              <MuiTooltip
                title={
                  interactive
                    ? (a.online
                        ? (t('live_inbox_team_set_offline') || 'Set offline')
                        : (t('live_inbox_team_set_online') || 'Set online'))
                    : (isSelf
                        ? ''
                        : (t('live_inbox_team_toggle_disabled') || 'Only this teammate can set themselves online.'))
                }
                arrow
                placement="top"
                enterDelay={300}
                enterNextDelay={150}
                disableInteractive
              >
                <span className="live-inbox-toggle-wrap">
                  <button
                    type="button"
                    className={`live-inbox-toggle ${a.online ? 'online' : 'offline'}${interactive ? '' : ' is-disabled'}`}
                    onClick={onClick}
                    disabled={!interactive || busy}
                    aria-disabled={!interactive || busy}
                  >
                    {busy ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={a.online ? faToggleOn : faToggleOff} />
                    )}
                    <span>{a.online
                      ? (t('live_inbox_online') || 'Online')
                      : (t('live_inbox_offline') || 'Offline')
                    }</span>
                  </button>
                </span>
              </MuiTooltip>
            </li>
          );
        };
        return (
          <div className="agent-team-modal-overlay" onClick={() => setRosterModalBot(null)}>
            <div className="agent-team-modal" onClick={(e) => e.stopPropagation()}>
              <div className="agent-team-modal-header">
                <div className="live-inbox-availability-title-block">
                  <span className="live-inbox-availability-title live-inbox-card-title">
                    <span>{t('bots_team_availability_title') || 'Team availability'}</span>
                    <InfoTooltip text={t('live_inbox_availability_tooltip') || 'Live agents who are online can be requested by visitors directly from the chat. You can set yourself online or offline at any time, and admins can take a teammate offline if needed.'} />
                  </span>
                  <span className="live-inbox-availability-desc">
                    {onlineCount > 0
                      ? (t('live_inbox_availability_desc_some_online') || 'At least one agent is online. Visitors can request a human agent.')
                      : (t('live_inbox_availability_desc_all_offline') || 'All agents are offline. Visitors cannot request a human agent.')
                    }
                  </span>
                </div>
                <span className="live-inbox-availability-count">
                  {onlineCount}/{sorted.length} {t('live_inbox_online') || 'online'}
                </span>
                <button
                  type="button"
                  className="agent-team-modal-close"
                  onClick={() => setRosterModalBot(null)}
                  aria-label={t('close')}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="agent-team-modal-body">
                {selfAgent && (
                  <div className="live-inbox-agent-section">
                    <div className="live-inbox-agent-section-header">
                      {t('live_inbox_my_availability') || 'My availability'}
                    </div>
                    <ul className="live-inbox-agent-list">
                      {renderRow(selfAgent)}
                    </ul>
                  </div>
                )}
                <div className="live-inbox-agent-section-divider" />
                <div className="live-inbox-agent-section">
                  <div className="live-inbox-agent-section-header">
                    {t('live_inbox_other_agents') || 'Other agents'}
                  </div>
                  {otherAgents.length === 0 ? (
                    <div className="live-inbox-agent-empty-block">
                      <div className="live-inbox-agent-empty">
                        {t('live_inbox_no_other_agents') || 'No other agents in this organization yet.'}
                      </div>
                      {canWrite() && (
                        <button
                          type="button"
                          className="live-inbox-invite-btn"
                          onClick={() => {
                            setRosterModalBot(null);
                            navigate('/team-members');
                          }}
                        >
                          <FontAwesomeIcon icon={faUserPlus} />
                          <span>{t('live_inbox_invite_team_members') || 'Invite team members'}</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <ul className={`live-inbox-agent-list${otherAgents.length > 3 ? ' is-scrollable' : ''}`}>
                      {otherAgents.map(renderRow)}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <WarningModal
        open={!!pendingForceOffline}
        onClose={() => setPendingForceOffline(null)}
        title={t('force_offline_title') || 'Set teammate offline?'}
        cancelText={t('cancel') || 'Cancel'}
        confirmText={t('force_offline_confirm') || 'Set offline'}
        danger
        onConfirm={() => {
          if (!pendingForceOffline) return;
          const { botId, userId } = pendingForceOffline;
          setPendingForceOffline(null);
          handleForceTeammateOffline(botId, userId);
        }}
      >
        {pendingForceOffline && (
          <>
            {t('force_offline_body_prefix') || 'Are you sure you want to set'}{' '}
            <strong>{pendingForceOffline.name}</strong>{' '}
            {t('force_offline_body_suffix') || 'offline?'}
            <br /><br />
            {t('force_offline_warning_prefix') || 'Note: only the teammate themselves can '}
            <strong>{t('force_offline_warning_bold') || 'set their status back to online'}</strong>
            {t('force_offline_warning_suffix') || ' from their own account.'}
          </>
        )}
      </WarningModal>

    </div>
  );
};

export default BotsOverview;
